import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';
import {
  AI_ASSISTANT_DEFAULT_URL,
  aiAssistantUrlCookieName,
  apiUrlCookieName,
  BASE_URL,
  BrowserStorage,
  Instance,
  isDevelopment,
  Path,
  SECONDS_IN_HOUR,
  SSO_URL
} from '@app/const/consts';
import { SigninContainer } from '../Signin/components/SigninContainer';
import { Loader } from '@infologistics/frontend-libraries';
import { useSelector } from 'react-redux';
import { IApplicationState } from '@app/store/types/commonTypes';

import styles from '../AuthNew.module.scss';
import { getBaseUrl, getIsMobile } from '@app/utils/utils';
import cn from 'classnames';

import tokensService from '@app/services/tokensService';
import TokensService from '@services/tokensService';
import Loadable from 'react-loadable';
import { SpinnerWrapper } from '@app/containers/Navigation';
import cookies from '@utils/cookies';
import { RedirectToStartPage } from '@app/containers';
import { Formik } from 'formik';
import AiAssistantUrlForm from '../Signin/AiAssistantUrlForm';
import BaseUrlForm from '../Signin/BaseUrlForm';
import { IAiAssistantUrlFormState, IBaseUrlFormState } from '../Signin/types';
import urls from '@app/const/urls';
import { ButtonFormUrl } from '@app/common/ButtonFormUrl';

const SigninForm = Loadable({
  loader: () => import('@views/auth/components/Signin/SigninForm'),
  loading: SpinnerWrapper,
});

const Forgot = Loadable({
  loader: () => import('@views/auth/components/Forgot'),
  loading: SpinnerWrapper,
});

const NepVerify = Loadable({
  loader: () => import('@views/auth/components/NepVerify'),
  loading: SpinnerWrapper,
});

export const LoginLayout = () => {
  const signinContainerRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const ssoUrl = `${SSO_URL}${urls.users.SSOLogout}`;

  const isLoading = useSelector((state: IApplicationState) => state.loader.isLoading);
  const ssoLogout = useSelector((state: IApplicationState) => state.user.ssoLogout);
  const instance = useSelector((state: IApplicationState) => state.utils.instance);
  const instanceRU = instance === Instance.RUSSIAN;

  const location = useLocation();
  const forgotPage = !!(location.pathname === Path.FORGOT);
  const nepVerifyPage = !!(location.pathname === Path.NEP_VERIFY);

  const [refReady, setRefReady] = useState(false);
  const [showAdblockNotification, setShowAdblockNotification] = useState(true);
  const [showPending, setShowPending] = useState(false);
  const [baseUrlState, setBaseUrlState] = useState<IBaseUrlFormState>({
    baseUrl: getBaseUrl(),
    isBoldText: !!cookies.get(apiUrlCookieName),
  });
  const [assistantUrlState, setAssistantUrlState] = useState<IAiAssistantUrlFormState>({
    assistantUrl: cookies.get(aiAssistantUrlCookieName) || AI_ASSISTANT_DEFAULT_URL,
    isBoldText: !!cookies.get(aiAssistantUrlCookieName),
  });
  const [isMobile, setIsMobile] = useState(getIsMobile());
  const [showFormUrlSettings, setShowFormUrlSettings] = useState(false);
  const [showFormUrl, setShowFormUrl] = useState(false);

  const userFromService = TokensService.getUserFromCookies();
  const accessToken = tokensService.getTokenFromCookies(BrowserStorage.TOKEN_ACCESS, userFromService);

  useEffect(() => {
    if (formRef?.current && ssoLogout) {
      formRef.current.submit();
    }
  }, [ssoLogout]);

  useEffect(() => {
    updateRefReady();
  }, []);

  useEffect(() => {
    let resizeObserver: ResizeObserver;
    const refSignin = signinContainerRef.current;
    if (refSignin) {
      resizeObserver = new ResizeObserver(() => {
        const showFormUrl = window.screen.height < refSignin.offsetHeight + 86;
        setIsMobile(getIsMobile() || window.screen.height < 403);
        setShowFormUrlSettings(showFormUrl);
        setShowFormUrl(!showFormUrl);
      });

      resizeObserver.observe(document.body);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const updateRefReady = (): void => {
    const isReady = Boolean(signinContainerRef?.current);

    if (refReady !== isReady) {
      setRefReady(isReady);
    }
  };

  const handleResetText = (baseUrl: string): void => {
    setBaseUrlState((prev) => ({
      ...prev,
      baseUrl,
      isBoldText: false,
    }));
  };

  const handleResetAssistantText = (assistantUrl: string): void => {
    setAssistantUrlState((prev) => ({
      ...prev,
      assistantUrl,
      isBoldText: false,
    }));
  };

  const handleResetFormBaseUrl = (): void => {
    setBaseUrlState({
      baseUrl: BASE_URL,
      isBoldText: false,
    });
    cookies.remove(apiUrlCookieName);
  };

  const handleResetFormAssistantUrl = (): void => {
    setAssistantUrlState({
      assistantUrl: AI_ASSISTANT_DEFAULT_URL,
      isBoldText: false,
    });
    cookies.remove(aiAssistantUrlCookieName);
  };

  const handleSubmitBaseUrl = (values: IBaseUrlFormState): void => {
    const { baseUrl } = values;

    setBaseUrlState({
      baseUrl,
      isBoldText: true,
    });
    cookies.set(apiUrlCookieName, baseUrl, {
      maxAge: SECONDS_IN_HOUR * 24,
      path: '/',
    });
  };

  const handleSubmitAssistantUrl = (values: IAiAssistantUrlFormState): void => {
    const assistantUrl = normalizeAssistantUrl(values.assistantUrl);

    setAssistantUrlState({
      assistantUrl,
      isBoldText: true,
    });
    cookies.set(aiAssistantUrlCookieName, assistantUrl, {
      maxAge: SECONDS_IN_HOUR * 24,
      path: '/',
    });
  };

  const handleSsoSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.currentTarget.submit();
  };

  return (
    <div className={styles.layout}>
      {isLoading ? (
        <Loader loading />
      ) : (
        <div className={cn('relative', { [styles.container_mobile]: isMobile || showFormUrlSettings })}>
          <SigninContainer
            refContainer={signinContainerRef}
            forgot={forgotPage}
            nepVerifyPage={nepVerifyPage}
            nepVerifyPageShowNotification={showAdblockNotification}
            nepVerifyPending={showPending}
            isMobile={isMobile}
            horizontalView={showFormUrlSettings}
          >
            <Switch>
              {accessToken && !nepVerifyPage && <Redirect exact to={'/'} />}
              <Route path={Path.SIGN_IN} exact={true} component={SigninForm} />
              <Route path={Path.FORGOT} exact={true} component={Forgot} />
              {instanceRU && (
                <Route
                  path={Path.NEP_VERIFY}
                  exact={true}
                  render={() => <NepVerify setShowNotification={setShowAdblockNotification} setIsPending={setShowPending} />}
                />
              )}

              <Route component={RedirectToStartPage} />
            </Switch>
          </SigninContainer>

          {isDevelopment && showFormUrl && refReady ? (
            <div
              style={{
                top:
                  !isMobile && !showFormUrlSettings
                    ? (signinContainerRef?.current?.offsetTop ?? 0) + (signinContainerRef?.current?.offsetHeight ?? 0) + 20
                    : 0,
              }}
              className={cn(
                'd-flex flex-column full-width',
                !isMobile && styles.formUrl__container,
                showFormUrlSettings && styles.formUrl__container_mobile,
              )}
            >
              <Formik initialValues={baseUrlState} onSubmit={handleSubmitBaseUrl} enableReinitialize c>
                {(props) => <BaseUrlForm {...props} resetText={handleResetText} resetForm={handleResetFormBaseUrl} />}
              </Formik>
              <Formik initialValues={assistantUrlState} onSubmit={handleSubmitAssistantUrl} enableReinitialize>
                {(props) => (
                  <AiAssistantUrlForm
                    {...props}
                    resetText={handleResetAssistantText}
                    resetForm={handleResetFormAssistantUrl}
                  />
                )}
              </Formik>
            </div>
          ) : null}

          {isDevelopment && showFormUrlSettings && <ButtonFormUrl showExit={showFormUrl} onClick={() => setShowFormUrl(!showFormUrl)} />}
        </div>
      )}

      <form
        ref={formRef}
        action={ssoUrl}
        method={'post'}
        onSubmit={(e) => handleSsoSubmit(e)}
        style={{
          display: 'none',
        }}
      >
        <button type='submit' />
      </form>
    </div>
  );
};

const normalizeAssistantUrl = (url: string): string => {
  const normalized = url.trim() || AI_ASSISTANT_DEFAULT_URL;

  return normalized.endsWith('/') ? normalized : `${normalized}/`;
};

export default LoginLayout;
